import React, { useState } from 'react';
import { X, Youtube, Plus, Trash2 } from 'lucide-react';
import youTubePlaylistService from '../services/YouTubePlaylistService';

const YouTubePlaylistImportModal = ({ isOpen, onClose, onSuccess }) => {
  const [playlistUrl, setPlaylistUrl] = useState('');
  const [playlistName, setPlaylistName] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState('');
  const [importProgress, setImportProgress] = useState('');

  if (!isOpen) return null;

  const handlePlaylistUrlChange = (e) => {
    const url = e.target.value;
    setPlaylistUrl(url);
    setError('');
    
    // Validate URL
    if (url && !youTubePlaylistService.isValidPlaylistUrl(url)) {
      setError('Please enter a valid YouTube playlist URL');
    }
  };

  const handleImport = async () => {
    if (!playlistUrl) {
      setError('Please enter a playlist URL');
      return;
    }
    
    if (!youTubePlaylistService.isValidPlaylistUrl(playlistUrl)) {
      setError('Please enter a valid YouTube playlist URL');
      return;
    }

    setIsImporting(true);
    setError('');
    setImportProgress('Fetching playlist data...');

    try {
      const result = await youTubePlaylistService.createPlaylistFromUserInput(
        playlistUrl,
        playlistName || `Imported Playlist (${new Date().toLocaleDateString()})`
      );

      console.log('Playlist import result:', result);
      setImportProgress(`Successfully imported ${result.summary.successfulSheets} songs!`);
      
      // Wait a moment to show success message
      setTimeout(() => {
        if (onSuccess) {
          onSuccess(result);
        }
        handleClose();
      }, 1500);

    } catch (err) {
      console.error('Playlist import error:', err);
      setError(`Failed to import playlist: ${err.message}`);
      setImportProgress('');
    } finally {
      setTimeout(() => {
        setIsImporting(false);
      }, 1500);
    }
  };

  const handleClose = () => {
    setPlaylistUrl('');
    setPlaylistName('');
    setError('');
    setImportProgress('');
    setIsImporting(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Youtube className="w-6 h-6 text-red-600 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">
                  Import YouTube Playlist
                </h3>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition duration-150 ease-in-out"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="playlist-url" className="block text-sm font-medium text-gray-700 mb-2">
                  YouTube Playlist URL
                </label>
                <input
                  type="url"
                  id="playlist-url"
                  value={playlistUrl}
                  onChange={handlePlaylistUrlChange}
                  disabled={isImporting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  placeholder="https://www.youtube.com/playlist?list=..."
                />
                <p className="mt-1 text-sm text-gray-500">
                  Paste the URL of the YouTube playlist you want to import
                </p>
              </div>

              <div>
                <label htmlFor="playlist-name" className="block text-sm font-medium text-gray-700 mb-2">
                  Setlist Name (Optional)
                </label>
                <input
                  type="text"
                  id="playlist-name"
                  value={playlistName}
                  onChange={(e) => setPlaylistName(e.target.value)}
                  disabled={isImporting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  placeholder="My Awesome Setlist"
                />
              </div>

              {/* Progress indicator */}
              {isImporting && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    <p className="text-sm text-blue-800">{importProgress}</p>
                  </div>
                </div>
              )}

              {/* Automatic processing info */}
              {!isImporting && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                  <p className="text-sm text-green-800">
                    <strong>Automatic Import:</strong> We'll automatically fetch all videos from your playlist, 
                    parse song titles and artists, and create sheets with YouTube audio links.
                  </p>
                </div>
              )}
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={handleImport}
              disabled={isImporting || !playlistUrl || !youTubePlaylistService.isValidPlaylistUrl(playlistUrl)}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isImporting ? 'Importing...' : 'Import Playlist'}
            </button>
            {!isImporting && (
              <button
                onClick={handleClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default YouTubePlaylistImportModal;